import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntercom } from 'react-use-intercom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as identityActions from 'actions/identityActions';
import { GO_TO_MY_REPORT, PRE_ENGAGEMENT } from 'constants/pageDictionary';


/**
 * ATTUNED-3343
 */
const IntercomChat = (props) => {
    const {
        actions, respondentData, currentPage
    } = props;
    const intercom = useIntercom();
    const [identity, setIdentity] = useState({ user_data: {}, account_data: [] });
    const [hasCompletedAssessment, setHasCompletedAssessment] = useState(null);

    const [userData, setUserData] = useState({
        email: null,
        is_owner: null,
        user_hash: null,
        first_name: null,
        last_name: null,
        has_completed_assessment: null,
        attuned_account_id: null,
        account_created_at: null,
        company: {
            company_id: null
        }
    });

    const canShowIntercom = useMemo(() => {
        if (!identity.is_authenticated) {
            console.debug('identity is not present');
        }

        const toShow = identity.is_authenticated
            && identity.user_data
            && identity.user_data.email === respondentData.respondent_email
            && identity.user_data.current_active_account_id === respondentData.accountId;

        if (!toShow) {
            console.debug('survey does not belong to identity');
        } else {
            console.debug('survey belongs to identity');
        }

        return toShow;
    }, [identity, respondentData]);

    // Get user's identity
    useEffect(() => {
        actions.getIdentity().then(setIdentity);
    }, []);

    // Mark assessment as completed
    useEffect(() => {
        if ([GO_TO_MY_REPORT, PRE_ENGAGEMENT].includes(currentPage)) {
            setHasCompletedAssessment(true);
        }
    }, [currentPage]);

    const getAccountCreatedAt = useCallback((accountId) => {
        return (identity.account_data.find((r) => (r.account_id === accountId)) || {}).created_at;
    }, [identity]);

    useEffect(() => {
        const identityEmail = identity.user_data ? identity.user_data.email : identity.respondent_data.email;
        setUserData({
            user_hash: identity.intercom_identity,
            email: identityEmail,
            is_owner: identity.is_owner,
            first_name: respondentData.firstName,
            last_name: respondentData.lastName,
            attuned_account_id: respondentData.accountId,
            account_created_at: getAccountCreatedAt(respondentData.accountId),
            language_override: respondentData.languageId === 3 ? 'ja' : 'en',
            has_completed_assessment: hasCompletedAssessment,
            company: {
                company_id: respondentData.accountId
            }
        });
    }, [identity, respondentData, hasCompletedAssessment]);

    const INTERCOM_SETTINGS = {
        alignment: 'right',
        horizontal_padding: 30,
        vertical_padding: 30
    };

    /**
     * Make sure Intercom is properly shutdown
     * Make sure Intercom is shown only to new sign-ups, and the owner of the survey
     */
    useEffect(() => {
        if (canShowIntercom) {
            intercom.boot(INTERCOM_SETTINGS);
            intercom.hide();

            return () => {
                intercom.hardShutdown();
            };
        }

        return () => {
        };
    }, [canShowIntercom]);

    /**
     * Update user data
     */
    useEffect(() => {
        if (canShowIntercom) {
            intercom.update({ customAttributes: userData, ...userData });
        }
    }, [userData, canShowIntercom]);

    return <span />;
};

IntercomChat.propTypes = {
    respondentData: PropTypes.object,
    currentPage: PropTypes.string
};

const mapStateToProps = (state) => ({
    respondentData: state.respondentNameReducer.toJS(),
    currentPage: state.pageReducer.get('goToPage')
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(Object.assign({}, identityActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(IntercomChat);
